import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Slider, Button, Grid } from "@mui/material";
import { useFirebase } from "contexts/Firebase";
import { useAuth } from "contexts/User";
import { doc, getDoc, setDoc } from "firebase/firestore";

type CoveragePreferences = {
    comprehensiveCoverage: number;
    dental: number;
    lowDeductible: number;
    lowPremium: number;
    mentalHealth: number;
    networkSize: number;
    vision: number;
};

const initialPreferences: CoveragePreferences = {
    comprehensiveCoverage: 0,
    dental: 0,
    lowDeductible: 0,
    lowPremium: 0,
    mentalHealth: 0,
    networkSize: 0,
    vision: 0,
};

const preferenceLabels: { [key in keyof CoveragePreferences]: string } = {
    comprehensiveCoverage: "Comprehensive Coverage",
    dental: "Dental",
    lowDeductible: "Low Deductible",
    lowPremium: "Low Premium",
    mentalHealth: "Mental Health",
    networkSize: "Network Size",
    vision: "Vision",
};

interface CoveragePreferencesProps {
    onPreferencesSaved?: () => void;
    onPreferencesChanged?: (hasUnsavedChanges: boolean) => void;
}

const CoveragePref: React.FC<CoveragePreferencesProps> = ({ 
    onPreferencesSaved,
    onPreferencesChanged 
}) => {
    const { firestore } = useFirebase();
    const { user } = useAuth();
    const [preferences, setPreferences] = useState<CoveragePreferences>(initialPreferences);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        const fetchPreferences = async () => {
            if (user?.uid) {
                const docRef = doc(firestore, `users/${user.uid}/answers/preferences`);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    const coveragePrefs: CoveragePreferences = {
                        comprehensiveCoverage: data.comprehensiveCoverage || 0,
                        dental: data.dental || 0,
                        lowDeductible: data.lowDeductible || 0,
                        lowPremium: data.lowPremium || 0,
                        mentalHealth: data.mentalHealth || 0,
                        networkSize: data.networkSize || 0,
                        vision: data.vision || 0,
                    };
                    setPreferences(coveragePrefs);
                }
            }
        };
        fetchPreferences();
    }, [firestore, user?.uid]);

    const updatePreferences = useCallback((key: keyof CoveragePreferences, value: number) => {
        setPreferences(prev => ({ ...prev, [key]: value }));
        setHasChanges(true);
        onPreferencesChanged?.(true);
    }, [onPreferencesChanged]);

    const savePreferences = useCallback(async () => {
        if (!user?.uid) {
            console.error("User is not authenticated");
            return;
        }

        const preferencesRef = doc(firestore, `users/${user.uid}/answers/preferences`);
        
        // Get existing data first to avoid overwriting the planType preferences
        const docSnap = await getDoc(preferencesRef);
        const existingData = docSnap.exists() ? docSnap.data() : {};
        
        // Merge the coverage preferences with existing data
        await setDoc(preferencesRef, {
            ...existingData,
            ...preferences,
        }, { merge: true });
        
        setHasChanges(false);
        onPreferencesChanged?.(false);
        onPreferencesSaved?.();
    }, [user?.uid, firestore, preferences, onPreferencesSaved, onPreferencesChanged]);

    return (
        <Box sx={{ width: '100%' }}>
            <Grid 
                container 
                spacing={1} 
                sx={{ 
                    width: '100%',
                    margin: '0 auto'
                }}
            >
                <Grid item xs={12}>
                    <Typography color="primary" variant="h5" sx={{mb:2}} fontWeight={600}>What plan features are important to you?</Typography>
                </Grid>
                
                <Grid item xs={12}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        width: '100%',
                        mb: 1
                    }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Not Important
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Indifferent
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Very Important
                        </Typography>
                    </Box>
                </Grid>
                
                {["lowPremium", "lowDeductible", "comprehensiveCoverage", "mentalHealth", "dental", "vision"].map((key) => {
                    const value = preferences[key as keyof CoveragePreferences];
                    const label = preferenceLabels[key as keyof CoveragePreferences];
                    return (
                        <Grid item xs={12} key={key}>
                            <Box sx={{ px: 0 }}>
                                <Typography>{label}</Typography>
                                <Slider
                                    value={value as number}
                                    onChange={(_, newValue) => updatePreferences(key as keyof CoveragePreferences, newValue as number)}
                                    min={0}
                                    max={0.9}
                                    step={0.1}
                                    marks={[
                                        { value: 0, label: '' },
                                        { value: 0.45, label: '' },
                                        { value: 0.9, label: '' }
                                    ]}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(v) => {
                                        if (v <= 0.3) return "Not Important";
                                        if (v <= 0.6) return "Somewhat Important";
                                        return "Very Important";
                                    }}
                                    sx={{
                                        '& .MuiSlider-markLabel': {
                                            fontSize: '0.75rem'
                                        },
                                        '& .MuiSlider-track': {
                                            color: '#1b4b43'
                                        },
                                        '& .MuiSlider-thumb': {
                                            color: '#1b4b43'
                                        }
                                    }}
                                />
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!hasChanges}
                    onClick={savePreferences}
                >
                    Save
                </Button>
            </Box>
        </Box>
    );
};

export default CoveragePref; 